// src/Pages/AdminActivities.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Fade,
  Grid,
  Paper,
  Typography,
  alpha,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Tooltip,
  Fab,
  Snackbar,
  Alert,
  Stack,
  Divider,
  TablePagination
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarMonth,
  AccessTime,
  School as SchoolIcon,
  EmojiEmotions,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ThumbUp,
  PhotoLibrary,
} from "@mui/icons-material";
import {
  activityCategories,
  getCategoryById,
} from "./activityData";
import AddActivityForm from "./AddActivityForm";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import apiEndpoints from "../../apiconfig";
import ActivityProgressForm from "./ActivityProgressForm";

const AdminActivities = () => {
  const role = localStorage.getItem("role"); // "admin" or "staff"
  const [viewMode, setViewMode] = useState(false);
  const [progressMode, setProgressMode] = useState(false);
  const [progressActivity, setProgressActivity] = useState(null);
  const user = JSON.parse(localStorage.getItem("user")); // contains guid, id, name, etc.
  const loggedUser = {
    role,
    id: user?.guid, // ⭐ staff guid is needed
    name:
      `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Unknown",
  };

  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState("");
  const [skillFilter, setSkillFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [readOnly, setReadOnly] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const { showSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(9);

  // Teacher dropdown options: B → "Edward Bowman"
  const teacherOptions = useMemo(() => {
    const map = new Map();

    activities.forEach((a) => {
      if (a.teacherId && a.teacherName) {
        map.set(a.teacherId, a.teacherName);
      }
    });

    return Array.from(map.entries()).map(([id, name]) => ({
      id,
      name,
    }));
  }, [activities]);

  const skillOptions = useMemo(() => {
    const set = new Set();

    activities.forEach((a) => {
      a.skills?.forEach((s) => set.add(s));
    });

    return Array.from(set);
  }, [activities]);

  const classOptions = useMemo(() => {
    const set = new Set();
    activities.forEach((a) => {
      if (a.className) set.add(a.className);
    });
    return Array.from(set);
  }, [activities]);

  const totalPhotos = useMemo(
    () => activities.reduce((acc, a) => acc + (a.photos?.length || 0), 0),
    [activities]
  );

  const totalSkills = useMemo(
    () => activities.reduce((acc, a) => acc + (a.skills?.length || 0), 0),
    [activities]
  );

  const totalLikes = useMemo(
    () => activities.reduce((acc, a) => acc + (a.likes || 0), 0),
    [activities]
  );

  const filteredActivities = useMemo(() => {
    return activities.filter((a) => {
      if (skillFilter !== "all" && !a.skills?.includes(skillFilter))
        return false;

      if (classFilter !== "all" && a.className !== classFilter) return false;

      if (teacherFilter !== "all" && `${a.teacherId}` !== `${teacherFilter}`)
        return false;

      if (!search) return true;

      const text = [
        a.title,
        a.className,
        a.notes,
        a.teacherName,
        ...(a.skills || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(search.toLowerCase());
    });
  }, [activities, skillFilter, classFilter, teacherFilter, search]);

  useEffect(() => {
    loadActivities();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const handleOpenAdd = () => {
    setEditingActivity(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (activity) => {
    setEditingActivity(activity);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingActivity(null);
  };
  const handleConfirmDelete = async () => {
    if (!selectedActivity) return;

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${apiEndpoints.activity}?guid=${selectedActivity.guid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (data.status === "deleted") {
        setActivities((prev) =>
          prev.filter((a) => a.guid !== selectedActivity.guid)
        );
        showSnackbar("Activity deleted successfully", "success");
      } else {
        showSnackbar(data.message || "Failed to delete", "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
    }

    setDeleteDialogOpen(false);
  };

  const loadActivities = async () => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${apiEndpoints.activity}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();

      if (Array.isArray(data)) {
        // Convert backend fields → UI format
        console.log("d", data);
        const converted = data.map((a) => ({
          guid: a.guid,
          id: a.id,
          title: a.title,
          description: a.description,
          category: a.category,
          photos: a.images || [],
          skills: a.skills || [],
          date: a.activity_date,

          // CLASS →
          class_guid: a.class_guid,
          class_name: a.class_name,
          className: a.class_name,

          // STAFF →
          teacherName: a.staff_name,
          teacherId: a.staff_guid,
          staff_guid: a.staff_guid,
          staff_name: a.staff_name,

          // ⭐ FIX BELOW → ADD THESE
          branch_guid: a.branch_guid,
          branch_name: a.branch_name,
        }));

        setActivities(converted);
      }
    } catch (error) {
      console.error("Fetch activities failed:", error);
    }
  };
  const handleSubmitProgress = async (payload) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(apiEndpoints.activityProgress, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === "success") {
        showSnackbar("Progress submitted successfully!", "success");
        setProgressMode(false);
        handleCloseDrawer();
      } else {
        showSnackbar(data.message || "Failed to submit progress", "error");
      }
    } catch (err) {
      showSnackbar("Error submitting progress", "error");
      console.error(err);
    }
  };

  const handleSubmitActivity = async (formData) => {
    const token = localStorage.getItem("token");

    // Convert skills + images into array
   const payload = {
     ...formData,
     images: formData.images || [],
     skills: formData.skills || [],
   };


    const isEdit = !!editingActivity?.guid;

    const url = `${apiEndpoints.activity}`;

    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === "success") {
        // Refresh list from server
        await loadActivities();
        showSnackbar(
          isEdit
            ? "Activity updated successfully"
            : "Activity created successfully",
          "success"
        );
      } else {
        showSnackbar(data.message || "Failed to save", "error");
      }
    } catch (error) {
      console.error("Submit error:", error);
      showSnackbar("Error submitting activity", "error");
    }

    handleCloseDrawer();
  };

  const getTeacherName = (id) =>
    teacherOptions.find((t) => t.id === id)?.name || "Unknown";

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* HEADER + FILTER BAR */}
        <Fade in timeout={700}>
          <Box
            sx={{
              py: 4,
              px: { xs: 2, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* LEFT SIDE — TITLE & SUBTEXT */}
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.7rem", md: "2.1rem" },
                  }}
                >
                  Activities Overview
                </Typography>

                <Typography sx={{ color: "text.secondary" }}>
                  Plan, review and monitor daily classroom activities.
                </Typography>
              </Box>
            </Box>

            {/* Top filters */}
            <Stack
              direction="row"
              flexWrap="wrap"
              rowGap={1.5}
              columnGap={1.5}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                placeholder="Search by title, teacher, class..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  minWidth: { xs: "100%", sm: 260 },
                  bgcolor: "rgba(255,255,255,0.5)",
                  borderRadius: "999px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "999px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl
                size="small"
                sx={{ minWidth: 160 }}
                disabled={readOnly}
              >
                <InputLabel>Skill Development</InputLabel>
                <Select
                  label="Skill Development"
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.5)",
                    borderRadius: "999px",
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {skillOptions.map((skill) => (
                    <MenuItem key={skill} value={skill}>
                      {skill}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Class</InputLabel>
                <Select
                  label="Class"
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.5)",
                    borderRadius: "999px",
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {classOptions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Teacher</InputLabel>
                <Select
                  label="Teacher"
                  value={teacherFilter}
                  onChange={(e) => setTeacherFilter(e.target.value)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.5)",
                    borderRadius: "999px",
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {teacherOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {/* RIGHT SIDE — FAB BUTTON */}
            <Fab
              color="primary"
              sx={{
                boxShadow: 3,
                width: 56,
              }}
              onClick={handleOpenAdd} // <-- your function for opening the drawer
            >
              <AddIcon />
            </Fab>
          </Box>
        </Fade>

        {/* STATS ROW */}
        <Fade in timeout={850}>
          <Grid
            container
            spacing={3}
            sx={{ mb: 4, px: { xs: 2, md: 4 } }}
            alignItems="stretch"
          >
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "30%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "18px",
                  background: "rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  height: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Total Activities
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {activities.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  Logged this month
                </Typography>
              </Paper>
            </Grid>

            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "30%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "18px",
                  background: "rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  height: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Photos Shared
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#4ECDC4" }}
                >
                  {totalPhotos}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <PhotoLibrary
                    sx={{ fontSize: 18, color: "#4ECDC4", mr: 0.5 }}
                  />
                  <Typography variant="caption" sx={{ color: "#888" }}>
                    Across all activities
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "30%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  height: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Skills Logged
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#FFB300" }}
                >
                  {totalSkills}
                </Typography>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  Different skill tags used
                </Typography>
              </Paper>
            </Grid>

            {/* <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "30%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  height: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Total Likes
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#FF6B6B" }}
                >
                  {totalLikes}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <ThumbUp sx={{ fontSize: 18, color: "#FF6B6B", mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: "#888" }}>
                    From parents & staff
                  </Typography>
                </Box>
              </Paper>
            </Grid> */}
          </Grid>
        </Fade>

        {/* ACTIVITIES GRID */}
        <Fade in timeout={1000}>
          <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
            {filteredActivities.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: "20px",
                  background: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  textAlign: "center",
                }}
              >
                <FilterIcon sx={{ fontSize: 40, color: "#bbb", mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No activities match your filters
                </Typography>
                <Typography variant="body2" sx={{ color: "#777" }}>
                  Try clearing filters or searching with a different term.
                </Typography>
              </Paper>
            ) : (
              <>
              <Grid container spacing={3}>
                {filteredActivities
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((a) => {
                    const category = getCategoryById(a.category);
                    const CatIcon = category?.icon;
                    return (
                      <Grid
                        item
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "50%",
                            md: "50%",
                            lg: "33%",
                            xl: "33%",
                          },
                        }}
                      >
                        <Card
                          sx={{
                            borderRadius: "18px",
                            overflow: "hidden",
                            background: "rgba(255,255,255,0.5)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(255,255,255,0.6)",
                            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-3px)",
                              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.2)",
                            },
                          }}
                        >
                          {a.photos?.length ? (
                            <CardMedia
                              component="img"
                              height="160"
                              image={a.photos[0]}
                              alt={a.title}
                              sx={{ objectFit: "cover" }}
                            />
                          ) : null}

                          <CardContent sx={{ flexGrow: 1 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              {CatIcon && (
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "10px",
                                    bgcolor: alpha(category.color, 0.12),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: category.color,
                                  }}
                                >
                                  <CatIcon fontSize="small" />
                                </Box>
                              )}
                              {/* <Typography
                              variant="subtitle2"
                              sx={{ color: "#555", fontWeight: 600 }}
                            >
                              {category?.label || "Activity"}
                            </Typography> */}
                            </Stack>

                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {a.title}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <CalendarMonth
                                sx={{ fontSize: 16, color: "#777" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#777" }}
                              >
                                {formatDate(a.date)}
                              </Typography>
                              <AccessTime
                                sx={{ fontSize: 16, color: "#777", ml: 1 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#777" }}
                              >
                                {a.time}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <SchoolIcon
                                sx={{ fontSize: 16, color: "#777" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#777" }}
                              >
                                {a.className || "Not set"}
                              </Typography>
                            </Stack>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "#555",
                                mb: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {a.notes}
                            </Typography>

                            {/* <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <EmojiEmotions
                              sx={{ fontSize: 16, color: "#FFB300" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: "#777" }}
                            >
                              Mood: {a.mood || "N/A"}
                            </Typography>
                          </Stack> */}

                            <Typography
                              variant="caption"
                              sx={{ color: "#777" }}
                            >
                              Teacher: {a.teacherName}
                            </Typography>

                            {a.skills?.length ? (
                              <Box
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {a.skills.slice(0, 3).map((s) => (
                                  <Chip
                                    key={s}
                                    label={s}
                                    size="small"
                                    sx={{
                                      bgcolor: "rgba(76,175,80,0.08)",
                                      color: "#2E7D32",
                                    }}
                                  />
                                ))}
                                {a.skills.length > 3 && (
                                  <Chip
                                    label={`+${a.skills.length - 3} more`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderStyle: "dashed" }}
                                  />
                                )}
                              </Box>
                            ) : null}
                          </CardContent>

                          <CardActions
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {/* <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <ThumbUp sx={{ fontSize: 16, color: "#FF6B6B" }} />
                            <Typography
                              variant="caption"
                              sx={{ color: "#777" }}
                            >
                              {a.likes || 0} likes
                            </Typography>
                          </Stack> */}
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setViewMode(true);
                                    setEditingActivity(a);
                                    setDrawerOpen(true);
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setViewMode(false);
                                    handleOpenEdit(a);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setViewMode(false);
                                    setSelectedActivity(a);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Submit Progress">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setProgressActivity(a);
                                    setProgressMode(true);
                                    setDrawerOpen(true);
                                  }}
                                >
                                  <EmojiEmotions fontSize="small" />{" "}
                                  {/* you can replace icon */}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>

              <Box
  sx={{
    display: "flex",
    justifyContent: {
      xs: "center",
      sm: "flex-end",
      md: "flex-end",
    },
    mt: 4,
  }}
>
  <TablePagination
    component="div"
    count={filteredActivities.length}
    page={page}
    onPageChange={handleChangePage}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={handleChangeRowsPerPage}
    rowsPerPageOptions={[6, 9, 18, 36]}
    sx={{
      width: "100%",
      ".MuiTablePagination-toolbar": {
        justifyContent: "space-between",
      },
    }}
  />
</Box>
</>
            )}
            
          </Box>
        </Fade>
      </Container>

      {drawerOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2500,
            display: "flex",
            justifyContent: "flex-end",
            bgcolor: "rgba(15, 23, 42, 0.25)", // darker overlay
            backdropFilter: "blur(4px)", // strong blur
            WebkitBackdropFilter: "blur(18px)", // Safari support
            transition: "all 0.3s ease",
          }}
          onClick={handleCloseDrawer}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: "85%", md: "60%", lg: "50%" },
              height: "100vh",
              p: { xs: 2, md: 3 },
              boxSizing: "border-box",
              display: "flex",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Paper
              elevation={6}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                bgcolor: "rgba(,255,255,0.96)",
                backdropFilter: "blur(18px)",
                boxShadow:
                  "0 18px 45px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148, 163, 184, 0.18)",
                display: "flex",
                flexDirection: "column",
                animation: "slideIn 0.35s ease",
              }}
            >
              {/* Add this container div */}
              <Box
                id="modal-container"
                sx={{ flex: 1, overflowY: "auto", pr: 1 }}
              >
                <IconButton
                  onClick={handleCloseDrawer}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "rgba(15,23,42,0.04)",
                    "&:hover": {
                      bgcolor: "rgba(15,23,42,0.08)",
                    },
                    zIndex: 10,
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* FORM CONTENT */}
                <Box
                  id="activity-modal-container"
                  sx={{ width: "100%", height: "100%" }}
                >
                  {progressMode ? (
                    <ActivityProgressForm
                      activity={progressActivity}
                      class_guid={progressActivity?.class_guid}
                      onSubmit={handleSubmitProgress}
                      onCancel={() => {
                        setProgressMode(false);
                        handleCloseDrawer();
                      }}
                    />
                  ) : (
                    <AddActivityForm
                      mode={
                        viewMode ? "view" : editingActivity ? "edit" : "add"
                      }
                      initialData={editingActivity}
                      onSubmit={handleSubmitActivity}
                      onCancel={handleCloseDrawer}
                      loggedUser={loggedUser}
                      readOnly={viewMode}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* ANIMATION CSS */}
        </Box>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Delete Activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default AdminActivities;
